import type { ComponentProps } from 'react';
import type {
  ProjectGraphDirectiveStatement,
  ProjectGraphDirectiveVerdict,
  ProjectGraphNode,
  ProjectGraphTaskSchedule,
  ProjectGraphTaskStatus,
} from '@avisoma/shared';
import type { Badge } from '../../catalyst/badge';

type BadgeColor = NonNullable<ComponentProps<typeof Badge>['color']>;

const STATUS_COLOR: Record<ProjectGraphTaskStatus, BadgeColor> = {
  overdue: 'red',
  'due-soon': 'amber',
  'on-track': 'green',
  'clocks-missing': 'zinc',
  'no-record': 'zinc',
};

/**
 * A task reference's badge: N/A, else its due status ("due soon"), else "unknown" when the sheet prints a value the
 * extractor could not read; none for other references or no status.
 */
export function taskBadge(node: ProjectGraphNode): { color: BadgeColor; label: string } | undefined {
  if (node.kind !== 'reference' || node.refKind !== 'task') return undefined;
  if (node.notApplicable) return { color: 'zinc', label: 'N/A' };
  const status = node.taskStatus;
  if (status !== undefined) return { color: STATUS_COLOR[status], label: status.replace(/-/g, ' ') };
  return node.unknown ? { color: 'zinc', label: 'unknown' } : undefined;
}

const VERDICT_BADGE: Record<ProjectGraphDirectiveVerdict, { color: BadgeColor; label: string }> = {
  complied: { color: 'green', label: 'complied' },
  open: { color: 'amber', label: 'open' },
  'record-missing': { color: 'red', label: 'record missing' },
  contradicted: { color: 'red', label: 'contradicted' },
};

/** An AD reference's compliance verdict badge; none for other references or an AD without a verdict. */
export function directiveBadge(node: ProjectGraphNode): { color: BadgeColor; label: string } | undefined {
  if (node.kind !== 'reference' || node.refKind !== 'ad' || node.verdict === undefined) return undefined;
  return VERDICT_BADGE[node.verdict];
}

const joined = (parts: (string | undefined)[], separator: string): string | undefined => {
  const kept = parts.filter((part): part is string => part !== undefined && part !== '');
  return kept.length === 0 ? undefined : kept.join(separator);
};

const unit = (value: number | undefined, label: string) => (value === undefined ? undefined : `${value} ${label}`);

type Schedule = ProjectGraphTaskSchedule;

function lastDoneText(lastDone: Schedule['lastDone']): string | undefined {
  if (!lastDone) return undefined;
  const shopVisit = lastDone.shopVisit === undefined ? undefined : `shop visit ${lastDone.shopVisit}`;
  return joined([lastDone.date, unit(lastDone.tsn, 'FH'), unit(lastDone.csn, 'FC'), shopVisit], ' · ');
}

function nextDueText(nextDue: Schedule['nextDue']): string | undefined {
  if (!nextDue) return undefined;
  const atShopVisit =
    nextDue.shopVisit !== undefined
      ? `at shop visit ${nextDue.shopVisit}`
      : nextDue.atShopVisit
        ? 'at shop visit'
        : undefined;
  return joined([nextDue.date, unit(nextDue.hours, 'FH'), unit(nextDue.cycles, 'FC'), atShopVisit], ' / ');
}

function intervalText(interval: Schedule['interval']): string | undefined {
  if (!interval) return undefined;
  const shopVisits = unit(interval.shopVisits, interval.shopVisits === 1 ? 'shop visit' : 'shop visits');
  return joined(
    [unit(interval.hours, 'FH'), unit(interval.cycles, 'FC'), unit(interval.days, 'days'), shopVisits],
    ' / ',
  );
}

/**
 * The schedule lines of a task: last done, next due, repeat interval, each with only the fields the sheet states, and
 * a last line when the sheet prints a value the extractor could not read.
 */
export function scheduleLines(schedule: Schedule | undefined, unknown = false): string[] {
  const done = lastDoneText(schedule?.lastDone);
  const due = nextDueText(schedule?.nextDue);
  const every = intervalText(schedule?.interval);
  return [
    done === undefined ? undefined : `Last done ${done}`,
    due === undefined ? undefined : `Next due ${due}`,
    every === undefined ? undefined : `Every ${every}`,
    unknown ? 'Unreadable value on the sheet' : undefined,
  ].filter((line): line is string => line !== undefined);
}

/** The statement lines of an AD: its status, compliance day and next due, each only when the sheet states it. */
export function directiveLines(directive: ProjectGraphDirectiveStatement | undefined): string[] {
  const due = nextDueText(directive?.nextDue);
  return [
    directive?.status === undefined ? undefined : `Status: ${directive.status.replace(/-/g, ' ')}`,
    directive?.complianceDate === undefined ? undefined : `Complied on ${directive.complianceDate}`,
    due === undefined ? undefined : `Next due ${due}`,
  ].filter((line): line is string => line !== undefined);
}
